/** Hands the signed-in reader's locale to the formatters, and re-draws everything when it changes.
 *
 * The one writer of `lib/format.ts`'s reader locale (ADR-0199, walkthrough defect C10: every date
 * in a California workspace read `31 Oct 2026`). The value comes from `GET /auth/me`, which the
 * session check has already made, so nothing here asks the server for anything and nothing waits.
 *
 * **It is set during render, on purpose.** A date is formatted while a screen renders, so the
 * locale has to be in place before the first child renders; an effect would run after, and the
 * first paint would be in the wrong order. The write is idempotent (the same value every time for
 * the same props), which is what makes it safe under StrictMode's double render.
 *
 * **A change remounts the children** (`key`). Formatted text is not state React can see change, so
 * a reader who switches from day-first to month-first on their profile would otherwise keep seeing
 * the old order until each component happened to re-render for another reason. Remounting is the
 * blunt, correct tool for a thing that changes once in a person's life.
 */

import { Fragment } from "react";
import type { ReactNode } from "react";

import { setReaderLocale } from "../lib/format";
import type { ReaderLocale } from "../lib/format";

export function ReaderLocaleScope({
  locale,
  children,
}: {
  locale: ReaderLocale | null | undefined;
  children: ReactNode;
}) {
  const known = locale === "en-US" || locale === "en-GB" ? locale : undefined;
  setReaderLocale(known);
  return <Fragment key={known ?? "unset"}>{children}</Fragment>;
}
